import { Component, DestroyRef, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import moment, { Moment } from 'moment';
import { DateInputValue, DateRangeControlValue, DateRangeProps, DateRangeSelection, PickerDate } from './date-range.model';

@Component({
    selector: 'formly-date-range',
    templateUrl: 'date-range.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatDatepickerModule]
})
export class FormlyDateRangeComponent extends FieldType<FieldTypeConfig<DateRangeProps>> implements OnInit {
    readonly range = new FormGroup({
        startDate: new FormControl<PickerDate>(null),
        endDate: new FormControl<PickerDate>(null),
    });
    minSelectableDate: Date | null = null;

    private readonly destroyRef = inject(DestroyRef);
    private endDateKey: string = 'endDate';
    private originalStartDate: Moment | null = null;
    private isPublishingRange: boolean = false;

    get startDateInputId(): string {
        return `${this.id}-start`;
    }

    get endDateInputId(): string {
        return `${this.id}-end`;
    }

    get startDateLabel(): string {
        return this.resolveInputLabel(this.props.placeholderStart, 'start date');
    }

    get endDateLabel(): string {
        return this.resolveInputLabel(this.props.placeholderEnd, 'end date');
    }

    ngOnInit(): void {
        this.endDateKey = this.props.endDateKey || 'endDate';
        this.range.controls.startDate.setValidators([
            Validators.required,
            (control: AbstractControl): ValidationErrors | null => this.validateStartDate(control)
        ]);
        this.range.controls.endDate.setValidators(Validators.required);

        this.range.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((selection: Partial<DateRangeSelection>) => this.publishRange(selection));

        this.formControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (!this.isPublishingRange) {
                    this.syncRangeFromModel();
                }
            });

        this.syncRangeFromModel();
    }

    private syncRangeFromModel(): void {
        const selection = this.readSelectionFromModel();
        this.originalStartDate = this.toStartOfDay(selection.startDate);
        this.minSelectableDate = this.resolveMinSelectableDate();
        this.range.setValue(selection, { emitEvent: false });

        if (this.isCompleteSelection(selection)) {
            this.publishRange(selection);
        }
    }

    private readSelectionFromModel(): DateRangeSelection {
        const current = this.formControl.value as DateInputValue | DateRangeControlValue;
        if (this.isRangeControlValue(current)) {
            return {
                startDate: this.toPickerDate(current.dates?.startDate),
                endDate: this.toPickerDate(current.dates?.endDate)
            };
        }

        const startDate = this.toPickerDate(current);
        return {
            startDate,
            endDate: startDate ? this.toPickerDate(this.formModel[this.endDateKey]) : null
        };
    }

    private publishRange(selection: Partial<DateRangeSelection>): void {
        const value: DateRangeControlValue | null = this.isCompleteSelection(selection)
            ? { dates: { startDate: selection.startDate, endDate: selection.endDate }, endDateKey: this.endDateKey }
            : null;

        this.isPublishingRange = true;
        this.formControl.setValue(value);
        this.isPublishingRange = false;
    }

    private isCompleteSelection(selection: Partial<DateRangeSelection>): selection is DateRangeSelection {
        return !!selection?.startDate && !!selection?.endDate && this.range.valid;
    }

    private validateStartDate(control: AbstractControl): ValidationErrors | null {
        if (!this.props.disallowPastDates || !control.value) {
            return null;
        }

        const selected = this.toStartOfDay(control.value as DateInputValue);
        if (!selected?.isBefore(moment().startOf('day'))) {
            return null;
        }

        return selected.isSame(this.originalStartDate, 'day') ? null : { startDateInPast: true };
    }

    private resolveMinSelectableDate(): Date | null {
        if (!this.props.disallowPastDates) {
            return null;
        }

        const today = moment().startOf('day');
        return this.originalStartDate?.isBefore(today) ? null : today.toDate();
    }

    private toPickerDate(value: DateInputValue): PickerDate {
        if (!value) {
            return null;
        }

        if (value instanceof Date || moment.isMoment(value)) {
            return value;
        }

        const parsed = moment(value);
        return parsed.isValid() ? parsed.toDate() : null;
    }

    private toStartOfDay(value: DateInputValue): Moment | null {
        if (!value) {
            return null;
        }

        const parsed = moment(value).startOf('day');
        return parsed.isValid() ? parsed : null;
    }

    private resolveInputLabel(placeholder: string | undefined, suffix: string): string {
        return placeholder || `${this.props.label ?? ''} ${suffix}`.trim();
    }

    private isRangeControlValue(value: DateInputValue | DateRangeControlValue): value is DateRangeControlValue {
        return !!value && typeof value === 'object' && 'dates' in value;
    }

    private get formModel(): Record<string, DateInputValue> {
        return (this.model ?? {}) as Record<string, DateInputValue>;
    }
}
